import React, {memo, useRef, useEffect, useState} from "react";
import {Select as AntSelect} from "antd";
import {useTranslation} from "react-i18next";
import cn from "classnames";
//local
import {InitialsBadge} from "components/InitialsBadge";
import {IconCloseRound} from "components/icons/index";
import {color as constColor} from "constants/color.consts";
import SelectSearchInput from "./SelectSearchInput";

const {Option} = AntSelect;

const Select = memo(
  ({
    placeholder, // placeholder
    title,
    name = "handy select",
    list,
    ownValue,
    initials, // show initials icon
    shadow, // inner select shadow
    round,
    small, // heigth 32px, font-size 12px
    tinyTitle, // font-size 10px
    smallTitle, // font-size 12px
    boldTitle,
    blackTitle,
    large, // heigth 36px
    huge, // heigth 40px
    smallFont, // font-size 12px
    tinyFont, // font-size 10px
    bold,
    uppercase,
    minWidth, // minimize left and right paddings
    color, // select color: green, purple, black, red
    showSearch,
    onChange,
    addClass,
    addSelectClass,
    addDropdownClass,
    alignRightDropdownRows,
    noMaxHeightDropdown,
    showArrow,
    disabled,
    fullWidth,
    value,
    defaultValue,
    fillSelectedItem, // fill selected item in Select
    transparentSelectedItem,
    needRenderDropdownIntoSelect, // render dropdown list component into select component in DOM
    children, // custom Option data
    customTopRow, // custom top dropdown area
    customBottomRow, // custom bottom dropdown area
    notFoundContent,
    prefixIcon,
    highlightIfEmpty,
    needSearch = false,
    searchPlaceholder = "Search",
    searchFieldBy = "label",
    ...props
  }) => {
    const {t} = useTranslation();

    const [key, setKey] = useState(0);
    const dropdownContainerRef = useRef(null);
    const [options, setOptions] = useState([]);
    const [isSearching, setIsSearching] = useState(false);

    const renderOptions = (items = []) => {
      if (!items.length) return [];

      return items.map(({value, label, icon}) => (
        <Option key={value} value={value}>
          {icon}
          {initials && (
            <InitialsBadge
              firstName={label}
              isCurrentUser={value === ownValue}
              addClass="mr-3 ml-1"
            />
          )}
          {t(label)}
        </Option>
      ));
    };

    useEffect(
      () => {
        if (!isSearching) {
          setOptions(renderOptions(list));
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [list, isSearching]
    );

    // for rerender dropdown
    useEffect(() => {
      setKey((prev) => prev + 1);
    }, [props.open]);

    const handleSearchResults = (filteredResults) => {
      if (needSearch) {
        setIsSearching(true);
        const newOptions = renderOptions(
          filteredResults.length ? filteredResults : renderEmptyOptions()
        );
        setOptions(newOptions);
      }
    };

    const handleDropdownVisibleChange = (open) => {
      if (!open) {
        setIsSearching(false);
        setOptions(renderOptions(list));
      }
      if (props.onDropdownVisibleChange) {
        props.onDropdownVisibleChange(open);
      }
    };

    const renderEmptyOptions = () => (
      <Option key="empty" value={t(notFoundContent)} disabled style={{display: "none"}}>
        {t(notFoundContent)}
      </Option>
    );

    return (
      <div className={addClass}>
        {title && (
          <p
            className={cn("selection__title", {
              __small: small || smallTitle,
              __large: large,
              __huge: huge,
              __tiny: tinyTitle,
              __bold: boldTitle,
              __black: blackTitle,
            })}
          >
            {t(title)}
          </p>
        )}
        <div ref={dropdownContainerRef} className="select">
          <AntSelect
            {...props}
            key={key}
            onChange={onChange}
            showAction={"focus"}
            placeholder={
              <React.Fragment>
                {prefixIcon}
                {t(placeholder)}
              </React.Fragment>
            }
            showSearch={!!showSearch}
            showArrow={!!showArrow}
            disabled={disabled}
            name={name}
            value={value}
            defaultValue={defaultValue}
            clearIcon={
              <IconCloseRound width={20} height={21} color={constColor[color]} />
            }
            notFoundContent={t(notFoundContent)}
            onDropdownVisibleChange={handleDropdownVisibleChange}
            filterOption={false}
            dropdownMatchSelectWidth={true}
            popupClassName={cn("editBlock_select_dropdown", {
              [addDropdownClass]: addDropdownClass,
              initials: !!initials,
              "align-rigth-row": alignRightDropdownRows,
              "no-max-height": noMaxHeightDropdown,
            })}
            dropdownRender={(menu) => (
              <React.Fragment>
                {needSearch && (
                  <SelectSearchInput
                    tiny
                    placeholder={t(searchPlaceholder)}
                    initialList={list || []}
                    fieldSearchBy={searchFieldBy}
                    listResultCallbackAfterSearch={handleSearchResults}
                  />
                )}
                {customTopRow}
                {menu}
                {customBottomRow}
              </React.Fragment>
            )}
            getPopupContainer={() =>
              needRenderDropdownIntoSelect ? dropdownContainerRef.current : document.body
            }
            className={cn({
              __small: small,
              __large: large,
              __huge: huge,
              __round: round,
              "__small-font": smallFont,
              "__tiny-font": tinyFont,
              __bold: bold,
              __uppercase: uppercase,
              [`__${color}`]: !!color,
              [addSelectClass]: !!addSelectClass,
              __shadow: shadow,
              "__show-arrow": showArrow,
              "__full-width": fullWidth,
              "__min-width": minWidth,
              "__filled-selected-item": fillSelectedItem,
              "__transparent-selected-item": transparentSelectedItem,
              "render-dropdown-into-select": !!needRenderDropdownIntoSelect,
              "__prefix-icon": prefixIcon,
              "bg-yellow": !!highlightIfEmpty,
            })}
          >
            {children ?? options}
          </AntSelect>
        </div>
      </div>
    );
  }
);

export default Select;
